import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Save, Zap, Shield, Loader2, User, KeyRound } from 'lucide-react';

function AccountSettings() {
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setMe(r.data);
      setEmail(r.data.email || '');
      setName(r.data.name || '');
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!currentPassword) { toast.error('Enter your current password to make changes'); return; }
    if (newPassword && newPassword !== confirmPassword) { toast.error('New passwords do not match'); return; }
    if (newPassword && newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (email !== me?.email) payload.email = email;
      if (name !== me?.name) payload.name = name;
      if (newPassword) payload.new_password = newPassword;
      const { data } = await api.put('/auth/account', payload);
      if (data.token) localStorage.setItem('sparky_admin_token', data.token);
      setMe(prev => ({ ...prev, email: data.email, name: data.name }));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Account updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setSaving(false);
  };

  if (!me) return null;

  return (
    <Card className="border-l-4 border-l-[#E8735A]" data-testid="account-settings">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-[#E8735A]" />Admin Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="flex items-center gap-1"><User className="w-3 h-3" />Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="account-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="flex items-center gap-1"><User className="w-3 h-3" />Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="account-email-input" />
          </div>
        </div>
        <Separator />
        <div className="space-y-1">
          <Label className="flex items-center gap-1"><KeyRound className="w-3 h-3" />Current Password <span className="text-destructive">*</span></Label>
          <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Required to save any changes" data-testid="account-current-password" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>New Password <span className="text-muted-foreground text-xs">(leave empty to keep current)</span></Label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" data-testid="account-new-password" />
          </div>
          <div className="space-y-1">
            <Label>Confirm New Password</Label>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" data-testid="account-confirm-password" />
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || !currentPassword} className="bg-[#E8735A] hover:bg-[#D4664F] text-white" data-testid="save-account-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save Account Changes
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/sparky-profile').then(r => setProfile(r.data)).catch(() => {});
  }, []);

  const update = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));
  const updateNested = (parent, key, value) => setProfile(prev => ({ ...prev, [parent]: { ...prev[parent], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/sparky-profile', profile);
      toast.success('Brand Profile saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (!profile) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-6 h-6 border-2 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit'] flex items-center gap-2">
            <Zap className="w-7 h-7 text-[#1A9DA5]" />Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage your account and AI generation defaults</p>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
          <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>

      {/* Admin Account */}
      <AccountSettings />

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-l-4 border-l-[#1A9DA5]">
          <CardHeader><CardTitle className="text-base">Brand Voice</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1"><Label>Brand Voice Description</Label><Textarea value={profile.brand_voice || ''} onChange={e => update('brand_voice', e.target.value)} rows={3} /></div>
            <div className="space-y-1"><Label>Content Direction</Label><Textarea value={profile.default_content_direction || ''} onChange={e => update('default_content_direction', e.target.value)} rows={2} /></div>
            <div className="space-y-1"><Label>Target Customer Types (comma-separated)</Label><Input value={(profile.default_target_customer_types || []).join(', ')} onChange={e => update('default_target_customer_types', e.target.value.split(',').map(t => t.trim()))} /></div>
            <div className="space-y-1">
              <Label>CTA Style</Label>
              <Select value={profile.default_cta_style || 'friendly-firm'} onValueChange={v => update('default_cta_style', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="soft">Soft Sell</SelectItem>
                  <SelectItem value="friendly-firm">Friendly-Firm</SelectItem>
                  <SelectItem value="hard">Hard Sell</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Preferred Frameworks</Label><Input value={profile.preferred_frameworks || ''} onChange={e => update('preferred_frameworks', e.target.value)} placeholder="e.g., problem-agitate-solve" /></div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-l-4 border-l-[#E8735A]">
            <CardHeader><CardTitle className="text-base">AI Model</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">Select the OpenAI model used for all AI content generation. Only applies when using your own OpenAI API key (OPENAI_API_KEY).</p>
              <div className="space-y-1">
                <Label>Model</Label>
                <Select value={profile.ai_model || 'gpt-4o'} onValueChange={v => update('ai_model', v)}>
                  <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o">GPT-4o (balanced)</SelectItem>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini (fast, cheaper)</SelectItem>
                    <SelectItem value="o3-mini">o3-mini (reasoning)</SelectItem>
                    <SelectItem value="gpt-4.1">GPT-4.1 (latest)</SelectItem>
                    <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
                    <SelectItem value="gpt-4.1-nano">GPT-4.1 Nano</SelectItem>
                    <SelectItem value="gpt-5.2">GPT-5.2 Thinking (advanced)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Custom Model ID <span className="text-muted-foreground text-xs">(optional, overrides selection above)</span></Label>
                <Input value={profile.ai_model_custom || ''} onChange={e => update('ai_model_custom', e.target.value)} placeholder="e.g., ft:gpt-4o:my-org:custom-model:id" className="font-mono text-xs" data-testid="ai-model-custom" />
                <p className="text-xs text-muted-foreground">Enter any valid OpenAI model ID. Useful for fine-tuned models or new releases.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Length Defaults</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Teaser Length (seconds)</Label><Input type="number" value={profile.default_lengths?.teaser_length_seconds || 90} onChange={e => updateNested('default_lengths', 'teaser_length_seconds', parseInt(e.target.value))} /></div>
                <div className="space-y-1"><Label>Full Length (minutes)</Label><Input type="number" value={profile.default_lengths?.full_length_minutes || 15} onChange={e => updateNested('default_lengths', 'full_length_minutes', parseInt(e.target.value))} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Structure Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1"><Label>Default Chapter Count</Label><Input type="number" value={profile.structure_prefs?.default_chapter_count || 5} onChange={e => updateNested('structure_prefs', 'default_chapter_count', parseInt(e.target.value))} /></div>
              {[['include_recap', 'Include Recap'], ['include_action_checklist', 'Include Action Checklist'], ['include_templates_examples', 'Include Templates/Examples']].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <Label>{label}</Label>
                  <Switch checked={profile.structure_prefs?.[key] ?? true} onCheckedChange={v => updateNested('structure_prefs', key, v)} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Compliance</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={profile.compliance_constraints || ''} onChange={e => update('compliance_constraints', e.target.value)} rows={2} placeholder="e.g., No legal/medical claims. Include disclaimer." />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
